import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { SvgIconComponentFactory } from 'projects/shared/svg-icons/svg-icon-component-factory';
import { SvgIconHostDirective } from 'projects/shared/svg-icons/svg-icon-host.directive';
import { TypeOfIncome } from '../../../models/enums/type-of-income.enum';
import { IncomeItem } from '../../../models/income-item-model';
import Swal from 'sweetalert2';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'income-item',
    templateUrl: 'income-item.component.html',
    styleUrls: ['./income-item.component.scss'],
    standalone: false
})
export class IncomeItemComponent implements OnInit, AfterViewInit {
  // static: false — the host directive lives inside a `*transloco` structural
  // directive, so a static query can't resolve it at ngOnInit time. Wait for
  // view init (ngAfterViewInit) to load the icon.
  @ViewChild(SvgIconHostDirective, { static: false })
  svgIconHost?: SvgIconHostDirective;

  @Input()
  income!: IncomeItem;

  /**
   * Reactive getter — recomputed on each change-detection cycle so the row
   * reflects the latest `income` Input (employer name after save/edit/refresh)
   * rather than a value captured once at ngOnInit time.
   */
  get incomeName(): string {
    return this.getIncomeName();
  }

  @Output() deleteClicked = new EventEmitter<IncomeItem>();
  @Output() editClicked = new EventEmitter<IncomeItem>();
  @Output() editIncomeItemsClicked = new EventEmitter<IncomeItem>();

  constructor(
    private readonly _svgIconFactory: SvgIconComponentFactory,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private readonly _translocoService: TranslocoService
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    this.loadIcon();
  }

  onDeleteClicked = (e: any) => {
    e.cancelBubble = true;
    if(e.stopPropagation) e.stopPropagation();

    Swal.fire({
      title: this._translocoService.translate('common.areYouSure'),
      text: this._translocoService.translate('wizard.income.confirmDeleteIncome', { incomeName: this.incomeName }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this._translocoService.translate('common.yesDeleteIt')
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteClicked.emit(this.income);
      }
    });
  };

  onEditClicked = () => {
    this.editClicked.emit(this.income);
  };

  onEditIncomeClicked = (e: any) => {
    e.cancelBubble = true;
    if(e.stopPropagation) e.stopPropagation();
    this.editIncomeItemsClicked.emit(this.income);
  }

  private loadIcon = () => {
    const iconName = this.getIconName();
    if (!iconName || !this.svgIconHost) {
      return;
    }
    const svgIconComponent = this._svgIconFactory.getSvIconComponent(iconName);
    if (!svgIconComponent) {
      return;
    }
    const componentFactory =
      this._componentFactoryResolver.resolveComponentFactory(svgIconComponent);

    const viewContainerRef = this.svgIconHost.viewContainerRef;
    viewContainerRef.clear();

    viewContainerRef.createComponent(componentFactory);
  };

  private getIncomeName(): string {
    if (this.income.name && this.income.name.length > 0) {
      return this.income.name;
    }
    return this.income.type;
  }

  private getIconName(): string {
    if (this.income.isSelfEmployed) {
      return 'duotone-safe';
    }
    if (this.income.type == TypeOfIncome.MilitaryBasePay || this.income.type == TypeOfIncome.MilitaryCombatPay) {
      return 'duotone-star';
    }
    if (this.income.type == TypeOfIncome.Base) {
      return 'duotone-shirt';
    }
    if (this.income.type == TypeOfIncome.ContractBasis) {
      return 'duotone-tools';
    }
    if (this.income.type == TypeOfIncome.NetRentalIncome) {
      return 'duotone-home';
    }
    if (this.income.type == TypeOfIncome.SocialSecurity) {
      return 'stockholm-bank';
    }
    if (this.income.type == TypeOfIncome.Pension) {
      return 'duotone-dollar';
    }
    if (this.income.type == TypeOfIncome.Disability) {
      return 'duotone-star';
    }
    if (this.income.type == TypeOfIncome.SelfEmploymentIncome) {
      return 'duotone-safe';
    }
    return 'duotone-wallet';
  }
}
